import { IBusinessEntitySummaryPage } from '@commenda/types';
import { SWRFetcher } from '@commenda/utils';
import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';

export interface ICorporationOption {
  id: number;
  displayName: string;
}

const CORPORATION_OPTIONS_PAGE_SIZE = 50;

function buildCorporationOptionsKey(
  search: string,
  limit: number,
  cursor?: number
) {
  const query = new URLSearchParams();
  query.append('limit', String(limit));
  query.append('includeNonControlled', 'true');
  if (search) {
    query.append('search', search);
  }
  if (cursor) {
    query.append('cursor', String(cursor));
  }
  return `business-entities/summaries?${query}`;
}

/** Cursor-paginated, server-searchable list of every company's entities, for the admin pickers. */
export function useCorporationOptions(
  search = '',
  pageSize = CORPORATION_OPTIONS_PAGE_SIZE
) {
  const trimmedSearch = search.trim();

  const getKey = (
    pageIndex: number,
    prevPage: IBusinessEntitySummaryPage | null
  ): string | null => {
    if (pageIndex === 0) return buildCorporationOptionsKey(trimmedSearch, pageSize);
    if (!prevPage?.nextCursor) return null;
    return buildCorporationOptionsKey(trimmedSearch, pageSize, prevPage.nextCursor);
  };

  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite<IBusinessEntitySummaryPage>(
      getKey,
      SWRFetcher({ method: 'GET' }, undefined),
      {
        revalidateFirstPage: false,
        keepPreviousData: true,
      }
    );

  const items = useMemo<ICorporationOption[]>(
    () =>
      Array.isArray(data)
        ? data.flatMap((page) =>
            (page?.entities ?? []).map((entity) => ({ id: entity.id, displayName: (entity.legalName ?? '').trimEnd() }))
          )
        : [],
    [data]
  );

  const lastPage = Array.isArray(data) ? data.at(-1) : undefined;
  const hasMore = !!lastPage?.nextCursor;
  // SWRInfinite leaves the slot for an in-flight page undefined until it resolves.
  const isLoadingMore = !!data && typeof data[size - 1] === 'undefined';

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  return {
    items,
    hasMore,
    loadMore,
    isLoading: !!isLoading,
    isLoadingMore,
    isValidating: !!isValidating,
    error,
    mutate,
  };
}
